import type { FoodContainer } from '../domain/model';
import {
  supportedRecipeLocale,
  type SupportedRecipeLocale,
} from '../content/recipe-messages';
import { localizePair } from '../i18n/catalog';

export const RECIPE_RETURN_PATH = '/recettes/preparer';
export const RECIPE_DEMO_PATH = '/demo';
export const RECIPE_LINK_CONTRACT_VERSION = 'frozen-save.recipe-links.v1';
export const RECIPE_CONTEXT_RETURN_MODE = 'recipe-context-v1';

const localRecipeTemplateIds = new Set(['soup', 'gratin', 'pan', 'pasta', 'tart', 'dessert']);

interface PublicRecipeProduct {
  slug: string;
  labels: Record<SupportedRecipeLocale, string>;
}

const product = (
  slug: string,
  french: string,
  english: string,
): PublicRecipeProduct => ({ slug, labels: { 'fr-FR': french, en: english } });

const approvedNameProducts: Array<{ terms: string[]; product: PublicRecipeProduct }> = [
  { terms: ['courgette'], product: product('courgettes', 'des courgettes', 'courgettes') },
  { terms: ['carotte'], product: product('carottes', 'des carottes', 'carrots') },
  { terms: ['haricot'], product: product('haricots', 'des haricots', 'beans') },
  { terms: ['epinard'], product: product('epinards', 'des épinards', 'spinach') },
  { terms: ['boeuf', 'bourguignon'], product: product('boeuf', 'du bœuf', 'beef') },
  { terms: ['porc'], product: product('porc', 'du porc', 'pork') },
  { terms: ['poulet'], product: product('poulet', 'du poulet', 'chicken') },
  { terms: ['dinde'], product: product('dinde', 'de la dinde', 'turkey') },
  { terms: ['canard'], product: product('canard', 'du canard', 'duck') },
  { terms: ['agneau'], product: product('agneau', 'de l’agneau', 'lamb') },
  { terms: ['veau'], product: product('veau', 'du veau', 'veal') },
  { terms: ['lapin'], product: product('lapin', 'du lapin', 'rabbit') },
  { terms: ['abat'], product: product('abats', 'des abats', 'offal') },
  { terms: ['saumon'], product: product('saumon', 'du saumon', 'salmon') },
  { terms: ['thon'], product: product('thon', 'du thon', 'tuna') },
  { terms: ['maquereau'], product: product('maquereau', 'du maquereau', 'mackerel') },
  { terms: ['cabillaud'], product: product('cabillaud', 'du cabillaud', 'cod') },
  { terms: ['colin'], product: product('colin', 'du colin', 'hake') },
  { terms: ['merlu'], product: product('merlu', 'du merlu', 'hake') },
  { terms: ['fraise'], product: product('fraises', 'des fraises', 'strawberries') },
];

const categoryProducts: Record<string, PublicRecipeProduct> = {
  meat: product('viandes', 'vos viandes', 'meat'),
  fish: product('poissons', 'vos poissons', 'fish'),
  vegetables: product('legumes', 'vos légumes', 'vegetables'),
  fruit: product('fruits', 'vos fruits', 'fruit'),
  prepared: product('plats-cuisines', 'vos plats cuisinés', 'prepared dishes'),
  bread: product('pains-et-patisseries', 'vos pains et pâtisseries', 'bread and pastries'),
  dessert: product('desserts', 'vos desserts', 'desserts'),
  dairy: product('produits-laitiers', 'vos produits laitiers', 'dairy products'),
  other: product('aliments', 'vos aliments', 'food'),
};

const directRecipeHubIds = new Set([
  'abats', 'agneau', 'boeuf', 'canard', 'carottes', 'courgettes', 'dinde', 'epinards',
  'lapin', 'porc', 'poulet', 'veau', 'viandes',
]);

const normalize = (value: string): string =>
  value
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('fr-FR');

export const publicRecipeProduct = (
  container: Pick<FoodContainer, 'name' | 'categoryId'>,
  locale: string,
): { slug: string; label: string } => {
  const normalizedName = normalize(container.name);
  const approved = approvedNameProducts.find(({ terms }) =>
    terms.some((term) => normalizedName.includes(term)),
  )?.product;
  const selected = approved ?? categoryProducts[container.categoryId] ?? categoryProducts.other!;
  return {
    slug: selected.slug,
    label: locale === 'en-XA'
      ? localizePair('en-XA', selected.labels['fr-FR'], selected.labels.en)
      : selected.labels[supportedRecipeLocale(locale)],
  };
};

export const normalizeRecipeSiteOrigin = (raw: string | null | undefined): string | null => {
  const value = raw?.trim();
  if (value == null || value === '') return null;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new Error('VITE_RECIPE_SITE_ORIGIN doit être une origine URL valide.');
  }
  const localHttp = url.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(url.hostname);
  if (url.protocol !== 'https:' && !localHttp) {
    throw new Error('VITE_RECIPE_SITE_ORIGIN doit utiliser HTTPS hors développement local.');
  }
  if (
    url.username !== '' ||
    url.password !== '' ||
    url.pathname !== '/' ||
    url.search !== '' ||
    url.hash !== ''
  ) {
    throw new Error('VITE_RECIPE_SITE_ORIGIN doit contenir uniquement une origine, sans chemin ni identifiants.');
  }
  return url.origin;
};

export const buildRecipeEditorialUrl = (
  origin: string | null,
  container: Pick<FoodContainer, 'name' | 'categoryId'>,
  locale: string,
): URL | null => {
  if (origin == null) return null;
  const selected = publicRecipeProduct(container, locale);
  const recipeLocale = supportedRecipeLocale(locale);
  const path = directRecipeHubIds.has(selected.slug)
    ? `/fr/recettes/${selected.slug}/`
    : '/fr/recettes/';
  const url = new URL(path, origin);
  url.searchParams.set('fs_source', 'app');
  url.searchParams.set('fs_lang', recipeLocale);
  return url;
};

const publicRecipeIdPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

interface RecipeReturnBase {
  publicRecipeId: string;
  locale: SupportedRecipeLocale;
}

export interface VerifiedRecipeReturn extends RecipeReturnBase {
  kind: 'verified';
  returnToken: string;
}

export interface EditorialRecipeReturn extends RecipeReturnBase {
  kind: 'editorial';
}

export interface ContextualRecipeReturn {
  kind: 'contextual';
  contractVersion: typeof RECIPE_LINK_CONTRACT_VERSION;
  destination: 'app' | 'demo';
  recipeId: string;
  ingredientId: string;
  localTemplateId: string | null;
  locale: SupportedRecipeLocale;
}

export type RecipeReturn = VerifiedRecipeReturn | EditorialRecipeReturn | ContextualRecipeReturn;

export const buildContextualRecipePath = (
  recipeReturn: ContextualRecipeReturn,
  destination: 'app' | 'demo',
): string => {
  const query = new URLSearchParams({
    fs_source: 'seo',
    fs_contract: RECIPE_LINK_CONTRACT_VERSION,
    fs_return: RECIPE_CONTEXT_RETURN_MODE,
    ingredient_id: recipeReturn.ingredientId,
    recipe_id: recipeReturn.recipeId,
    fs_lang: recipeReturn.locale,
  });
  if (recipeReturn.localTemplateId != null) {
    query.set('template_id', recipeReturn.localTemplateId);
  }
  return `${destination === 'demo' ? RECIPE_DEMO_PATH : RECIPE_RETURN_PATH}?${query.toString()}`;
};

export const parseRecipeReturn = (url: URL): RecipeReturn | null => {
  if (url.searchParams.get('fs_source') !== 'seo') {
    return null;
  }
  const contractVersion = url.searchParams.get('fs_contract');
  if (contractVersion === RECIPE_LINK_CONTRACT_VERSION) {
    const destination = url.pathname === RECIPE_RETURN_PATH
      ? 'app'
      : url.pathname === RECIPE_DEMO_PATH
        ? 'demo'
        : null;
    const recipeId = url.searchParams.get('recipe_id');
    const ingredientId = url.searchParams.get('ingredient_id');
    const templateId = url.searchParams.get('template_id');
    if (
      destination == null ||
      url.searchParams.get('fs_return') !== RECIPE_CONTEXT_RETURN_MODE ||
      recipeId == null ||
      ingredientId == null ||
      recipeId.length > 80 ||
      ingredientId.length > 80 ||
      !publicRecipeIdPattern.test(recipeId) ||
      !publicRecipeIdPattern.test(ingredientId) ||
      (templateId != null && (templateId.length > 80 || !publicRecipeIdPattern.test(templateId)))
    ) {
      return null;
    }
    return {
      kind: 'contextual',
      contractVersion: RECIPE_LINK_CONTRACT_VERSION,
      destination,
      recipeId,
      ingredientId,
      localTemplateId: templateId != null && localRecipeTemplateIds.has(templateId)
        ? templateId
        : null,
      locale: supportedRecipeLocale(url.searchParams.get('fs_lang') ?? 'fr-FR'),
    };
  }
  if (contractVersion != null || url.pathname !== RECIPE_RETURN_PATH) return null;
  const publicRecipeId = url.searchParams.get('recipe_id');
  const returnToken = url.searchParams.get('return_token');
  if (
    publicRecipeId == null ||
    publicRecipeId.length > 80 ||
    !publicRecipeIdPattern.test(publicRecipeId)
  ) {
    return null;
  }
  const locale = supportedRecipeLocale(url.searchParams.get('fs_lang') ?? 'fr-FR');
  if (returnToken == null) {
    return url.searchParams.get('fs_return') === 'local-list-v1'
      ? { kind: 'editorial', publicRecipeId, locale }
      : null;
  }
  if (!/^[A-Za-z0-9_-]{32}$/u.test(returnToken)) return null;
  return {
    kind: 'verified',
    publicRecipeId,
    locale,
    returnToken,
  };
};
